import React, { memo, useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import UserState from "../../../slices/user/UserState";
import { userSelector } from "../../../slices/user/userSlice";
import { toast } from "react-toastify";
import { Form, Formik, FormikProps } from "formik";
import XGSFormInput from "../../../ui-components/form/input/xgsFormInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import moment from "moment";
import dateFormats from "../../../app/data/common/dateFormats";
import Table from "../../../ui-components/table/table";
import { BillingReviewFiltersSchema, BillingReviewWorkItemFilters, BillingReviewWorkItemRequest, ShipmentWorkItem, ShipmentWorkItemReviewStatus } from "../../../app/data/shipment-reconciliation/models";
import { BillingReviewListState, BillingReviewSummaryState, ExportBillingReviewListState } from "../../../slices/shipment-reconciliation/ShipmentReconciliationState";
import { getBillingReviewListSelector, getBillingReviewWorkItems, resetList } from "../../../slices/shipment-reconciliation/billing-review/getBillingReviewListSlice";
import { getBillingReviewSummaryItems, getBillingReviewSummarySelector } from "../../../slices/shipment-reconciliation/billing-review/getBillingReviewSummarySlice";
import { getBillingReviewListColumns } from "./billingReviewListColumns";
import "./billingReview.scss";
import BillingReviewItemDetails from "./details/billingReviewItemDetails";
import { BillingReviewSummary } from "./summary/billingReviewSummary";
import DownloadButton from "ui-components/download-button/downloadButton";
import { downloadBillingReviewCsv, exportBillingReviewListSelector } from "slices/shipment-reconciliation/billing-review/exportBillingReviewListSlice";

const initialFormValues = {
  probillNumber: "",
  etaStartDate: "",
  etaEndDate: "",
  shipperName: "",
  consigneeName: ""
};

const BillingReview: React.FC<{}> = memo(() => {
  const dispatch = useDispatch();
  const [selectedShipmentWorkItemDetails, setSelectedShipmentWorkItemDetails] = useState<ShipmentWorkItem | null>(null);
  const [filterProbillNumber, setFilterProbillNumber] = useState<string | null | undefined>(null);
  const [filterEtaStartDate, setFilterEtaStartDate] = useState<string | null | undefined>(null);
  const [filterEtaEndDate, setFilterEtaEndDate] = useState<string | null | undefined>(null);
  const [filterShipper, setFilterShipper] = useState<string | null | undefined>(null);
  const [filterConsignee, setFilterConsignee] = useState<string | null | undefined>(null);
  const [billingReviewStatus, setBillingReviewStatus] = useState<string | null | undefined>(ShipmentWorkItemReviewStatus.PENDING);

  const formRef = React.useRef<FormikProps<BillingReviewWorkItemFilters>>(null);

  const userState: UserState = useSelector(
    userSelector
  );
  const getBillingReviewList: BillingReviewListState = useSelector(
    getBillingReviewListSelector
  );
  const getBillingReviewSummary: BillingReviewSummaryState = useSelector(
    getBillingReviewSummarySelector
  );
  const exportBillingReviewListState: ExportBillingReviewListState = useSelector(
    exportBillingReviewListSelector
  );

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "shipment_recon_billing" });
    const userTerminal = userState.activeTerminal?.id;
    onTerminalChanged(userTerminal);
    // eslint-disable-next-line
  }, [userState.activeTerminal?.id])

  const onTerminalChanged = (userTerminal: number | null | undefined) => {
    dispatch(resetList());
    if (!userTerminal) return; // can not get the Probills without knowing the terminal
    formRef.current?.submitForm();
  };

  const showShipmentWorkItemDetails = (shipmentWorkItem: ShipmentWorkItem) => {
    setSelectedShipmentWorkItemDetails(shipmentWorkItem);
  }

  const getFiltersRequest = (
    probillNumber: number | string | null | undefined,
    etaStartDate: string | null | undefined,
    etaEndDate: string | null | undefined,
    shipperName?: string | null | undefined,
    consigneeName?: string | null | undefined,
    billingReviewStatus?: string | null | undefined,
  ): BillingReviewWorkItemRequest => {
    let request: BillingReviewWorkItemRequest = {};
    if (probillNumber) request.probillNumber = probillNumber;
    if (etaStartDate) request.etaStartDate = moment(etaStartDate).format(dateFormats.isoDate);
    if (etaEndDate) request.etaEndDate = moment(etaEndDate).format(dateFormats.isoDate);
    if (shipperName) request.shipperName = shipperName;
    if (consigneeName) request.consigneeName = consigneeName;
    if (billingReviewStatus) request.billingReviewStatus = billingReviewStatus;
    return request;
  }

  const onSubmit = (data: BillingReviewWorkItemFilters) => {
    let requestData: BillingReviewWorkItemRequest = getFiltersRequest(
      data.probillNumber,
      data.etaStartDate,
      data.etaEndDate,
      data.shipperName,
      data.consigneeName
    );
    dispatch(getBillingReviewSummaryItems(
      { ...requestData },
      (error) => { toast.error(error) }
    ));
    if (billingReviewStatus) requestData.billingReviewStatus = billingReviewStatus;
    dispatch(getBillingReviewWorkItems(
      { ...requestData },
      (error) => { toast.error(error) }
    ));
  };

  const onListInfiniteScroll = () => {
    if (!getBillingReviewList.hasNext) return;
    let requestData: BillingReviewWorkItemRequest = getFiltersRequest(
      filterProbillNumber,
      filterEtaStartDate,
      filterEtaEndDate,
      filterShipper,
      filterConsignee,
      billingReviewStatus
    );
    requestData.cursor = getBillingReviewList.nextCursor;
    dispatch(getBillingReviewWorkItems(
      requestData,
      (error) => { toast.error(error) }
    ));
  }

  const onSummaryApply = (billingReviewStatus: ShipmentWorkItemReviewStatus) => {
    setBillingReviewStatus(billingReviewStatus);
    let requestData: BillingReviewWorkItemRequest = getFiltersRequest(
      filterProbillNumber,
      filterEtaStartDate,
      filterEtaEndDate,
      filterShipper,
      filterConsignee,
    );
    requestData.billingReviewStatus = billingReviewStatus;
    dispatch(getBillingReviewWorkItems(
      requestData,
      (error) => { toast.error(error) }
    ));
  }

  const onClearFilters = () => {
    setFilterProbillNumber(null);
    setFilterEtaStartDate(null);
    setFilterEtaEndDate(null);
    setFilterShipper(null);
    setFilterConsignee(null);
    setBillingReviewStatus(null);
    formRef.current?.resetForm();
  }

  const showFiltersNotification = () => {
    return !!(
      filterProbillNumber ||
      filterEtaStartDate ||
      filterEtaEndDate ||
      filterShipper ||
      filterConsignee ||
      billingReviewStatus
    );
  }

  const exportBilllingReviewList = () =>{
    let requestData: BillingReviewWorkItemRequest = getFiltersRequest(
      filterProbillNumber,
      filterEtaStartDate,
      filterEtaEndDate,
      filterShipper,
      filterConsignee,
      billingReviewStatus
    );
    dispatch(downloadBillingReviewCsv(
      requestData,
      (error) => { toast.error(error) }
    ));
  }

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          title="Billing Review"
          mods={{ "full-width": true }}
        >
           <DownloadButton
           title="Export CSV"
           spinner={exportBillingReviewListState.loading}
           disabled={exportBillingReviewListState.loading}
           onClick={() => exportBilllingReviewList()}
       />
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-billing-review">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <Formik
              initialValues={initialFormValues}
              onSubmit={onSubmit}
              validationSchema={BillingReviewFiltersSchema}
              innerRef={formRef}
            >
              {(props: FormikProps<BillingReviewWorkItemFilters>) => (
                <Form className="xgs-list__controls__search-form">
                  <XGSFormInput
                    type="text"
                    name="probillNumber"
                    label="Probill"
                    placeholder="Probill Number"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    onChange={(e) => {
                      props.setFieldValue("probillNumber", e?.currentTarget?.value || undefined);
                      setFilterProbillNumber(e?.currentTarget?.value);
                    }
                    }
                  />
                  <LabeledDateRangeInput
                    label="ETA Range"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    start={props.values.etaStartDate}
                    end={props.values.etaEndDate}
                    onStartChange={(v) => {
                      props.setFieldValue("etaStartDate", v || undefined);
                      setFilterEtaStartDate(v || null);
                    }}
                    onEndChange={(v) => {
                      props.setFieldValue("etaEndDate", v || undefined);
                      setFilterEtaEndDate(v || null);
                    }}
                  />
                  <XGSFormInput
                    type="text"
                    name="shipperName"
                    label="Shipper"
                    placeholder="Search Shipper Name"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    onChange={(e) => {
                      props.setFieldValue("shipperName", e.currentTarget.value);
                      setFilterShipper(e.currentTarget.value);
                    }
                    }
                  />
                  <XGSFormInput
                    type="text"
                    name="consigneeName"
                    label="Consignee"
                    placeholder="Search Consignee Name"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    onChange={(e) => {
                      props.setFieldValue("consigneeName", e.currentTarget.value);
                      setFilterConsignee(e.currentTarget.value);
                    }
                    }
                  />
                  <Button
                    type="submit"
                    theme={ButtonThemes.blue}
                    spinner={false}
                    disabled={getBillingReviewList.requestStarted || getBillingReviewList.fetchPortionStarted}
                  >
                    Search
                  </Button>
                  <Button
                    theme={ButtonThemes.gray}
                    spinner={false}
                    disabled={getBillingReviewList.requestStarted || getBillingReviewList.fetchPortionStarted}
                    onClick={onClearFilters}
                  >
                    Clear
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        <div className="xgs-billing-review__summary">
          <BillingReviewSummary
            summaryData={getBillingReviewSummary.summary}
            isLoading={getBillingReviewSummary.loading}
            onApply={onSummaryApply}
            isFiltersApplied={showFiltersNotification()}
          />
        </div>
        <div className="xgs-billing-review__table-wrapper">
          <Table
            isLoading={getBillingReviewList.requestStarted}
            columns={getBillingReviewListColumns()}
            data={getBillingReviewList.shipmentWorkItems}
            cursorPointer={true}
            rowHeight={64}
            noResultsText="No Shipments available"
            onRowClicked={(row: ShipmentWorkItem) => { showShipmentWorkItemDetails(row) }}
            onSelectedRowsChange={null}
            infiniteScroll
            infiniteScrollLoading={getBillingReviewList.fetchPortionStarted}
            infiniteScrollHasNext={getBillingReviewList.hasNext}
            onInfiniteScroll={onListInfiniteScroll}
            responsive={true}
            minTableHeight={400}
          />
        </div>
        {(selectedShipmentWorkItemDetails && selectedShipmentWorkItemDetails.id) && (
          <BillingReviewItemDetails
            shipmentWorkItemId={selectedShipmentWorkItemDetails.id}
            probillNumber={+selectedShipmentWorkItemDetails.probillNumber}
            terminalAirportCode={selectedShipmentWorkItemDetails.terminalAirportcode}
            terminalName={selectedShipmentWorkItemDetails.terminalName}
            terminalNumber={selectedShipmentWorkItemDetails.terminalNumber}
            onClose={() => { setSelectedShipmentWorkItemDetails(null) }}
          />
        )}
      </div>
    </ContentContainer>
  );
});

export default BillingReview;
